import React, { useState, useEffect, useCallback, useRef } from 'react'; // Import useRef
import { Spin } from 'antd'; // Import Spin
import { ReactComponent as DirectoryLogo } from "../images/directory.svg"; // Import DirectoryLogo
import { ReactComponent as ArrowLogo } from "../images/arrow.svg"; // Import ArrowLogo
import AccountSettings from './AccountSettings';
import BusinessSettings from './BusinessSettings';
import SurveyQuestionsSettings from './SurveyQuestionsSettings';
import ReferralLinksSettings from './ReferralLinksSettings';
import AppearanceSettings from './AppearanceSettings';
import TemplateSettings from './TemplateSettings';
import './styles/SettingsPage.css'; // Added CSS import
import { API_URLS, PREVIEW_CONFIG } from '../config'; // Import API_URLS and PREVIEW_CONFIG

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('account');
  const [logoUrl, setLogoUrl] = useState('');
  const [headerUrl, setHeaderUrl] = useState('');
  const [accentColor, setAccentColor] = useState('#F4A460');
  const [name, setName] = useState(localStorage.getItem('name') || '');
  const [businessName, setBusinessName] = useState(localStorage.getItem('businessName') || ''); // Get businessName from localStorage
  const [email, setEmail] = useState(localStorage.getItem('email') || '');
  const [previewPage, setPreviewPage] = useState(''); // Default preview page changed to 'landing'
  const [customQuestions, setCustomQuestions] = useState([]); // Initialize as empty array
  const [customLinks, setCustomLinks] = useState([]); // Add state for referral links
  const [businessId, setBusinessId] = useState(localStorage.getItem('businessId')); // Get businessId from localStorage
  const [isPreviewLoading, setIsPreviewLoading] = useState(false); // State for preview loading
  const [templateConfig, setTemplateConfig] = useState(null); // Add template config state

  // Ref to track initial mount
  const isInitialMount = useRef(true);
  // console.log("Base URL:", PREVIEW_CONFIG.baseUrl); // Log the base URL for debugging

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const businessName = localStorage.getItem('businessName'); // Get businessName from localStorage

        // Fetch business info (name, images, accent color) endpoint branding
        const businessInfoResponse = await fetch(API_URLS.branding(businessId), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!businessInfoResponse.ok) {
          throw new Error(`HTTP error fetching business info! status: ${businessInfoResponse.status}`);
        }

        const businessData = await businessInfoResponse.json();
        console.log("Business Info Response:", businessData); // Log the response
        // Assuming businessData is an array with one object like in frontRate Header.js
        if (businessData && businessData !== null && businessData.length > 0) {
          setLogoUrl(businessData[0].logo_image_link || '');
          setHeaderUrl(businessData[0].header_image_link || '');
          setAccentColor(
            businessData[0].accent_color && businessData[0].accent_color !== 'undefined'
              ? businessData[0].accent_color
              : '#F4A460'
          );
          setBusinessName(businessData[0].business_name || businessName);
        } else {
          setLogoUrl('');
          setHeaderUrl('');
          setAccentColor('#F4A460');
        }

        // Fetch custom questions
        if (businessId && token) {
          const questionsResponse = await fetch(API_URLS.questions(businessId), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!questionsResponse.ok) {
            throw new Error(`HTTP error fetching questions! status: ${questionsResponse.status}`);
          }
          const questionsData = await questionsResponse.json();
          // Format questions if necessary, similar to SurveyQuestionsSettings
          const formattedQuestions = questionsData.map(q => ({
            id: q.question_id,
            topic: q.topic_name || `Topic ${q.topic_id}`,
            text: q.question_text,
            topicId: q.topic_id
          }));
          setCustomQuestions(formattedQuestions);

          // Fetch referral links
          const linksResponse = await fetch(API_URLS.referralLinks(businessId), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!linksResponse.ok) {
            console.warn(`HTTP error fetching referral links! status: ${linksResponse.status}`);
            setCustomLinks([]);
          } else {
            const linksData = await linksResponse.json();
            // Format links using data directly from API
            const formattedLinks = linksData.map(link => {
              return {
                id: link.provider_id, // Use provider_id as id
                name: link.provider_name || `Provider ${link.provider_id}`, // Use name from API or fallback
                url: link.link_url || '',
                icon: link.provider_image || '', // Use image from API
                src: link.provider_image || '', // Use image from API for LeaveFeedbackPage compatibility
                show: false // Add show for LeaveFeedbackPage compatibility
              };
            });
            setCustomLinks(formattedLinks);

          // Fetch template configuration
          const templateResponse = await fetch(API_URLS.template(businessId), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (templateResponse.ok) {
            const templateData = await templateResponse.json();
            if (templateData && templateData.template_config) {
              setTemplateConfig(JSON.parse(templateData.template_config));
            }
          } else {
            setTemplateConfig(null);
          }
          }

        } else {        setCustomQuestions([]);
        setCustomLinks([]);
        setTemplateConfig(null);
        }

      } catch (error) {
        console.error('Error fetching settings:', error);
        // Set default values if fetch fails
        setLogoUrl('');
        setHeaderUrl('');
        setAccentColor('#F4A460');
        setEmail('');
        setCustomQuestions([]); // Set empty on error
        setCustomLinks([]); // Set empty on error
        setTemplateConfig(null); // Set empty on error
      }
    };

    fetchSettings();
  }, [businessId]); // Dependency array remains the same

  // Renamed and simplified: Triggers the iframe reload process
  const triggerPreviewRefresh = useCallback(() => {
    setIsPreviewLoading(true); // Start loading before iframe src change

    const iframe = document.getElementById('frontrate-preview');
    if (iframe) {
      // Force reload by changing the src slightly (add timestamp)
      const currentSrc = iframe.src.split('?')[0];
      const timestamp = new Date().getTime();
      // Ensure the previewPage state is incorporated into the new src
      const pageQueryParam = previewPage ? `&page=${previewPage}` : '';
      iframe.src = `${currentSrc}?preview=true&refresh=${timestamp}${pageQueryParam}`;
      // console.log(`Triggering iframe reload with src: ${iframe.src}`);
    }
  }, [previewPage]); // Add previewPage as a dependency

  // Effect to listen for iframe readiness and send configuration data
  useEffect(() => {
    const handleIframeMessage = (event) => {
      const iframe = document.getElementById('frontrate-preview'); // Get current iframe instance

      // Basic security checks
      if (event.origin !== PREVIEW_CONFIG.allowedOrigin || event.source !== iframe?.contentWindow) {
        // console.log("Ignoring message from unexpected source:", event.origin, event.source);
        return;
      }

      if (event.data.type === 'PREVIEW_READY') {
        console.log("Preview iframe is ready, sending config...");
        // const previewColumn = document.querySelector('.preview-column');

        // Send the *current* state data when the message is received
        if (iframe && iframe.contentWindow) {
          try {
            // Log the exact data being sent
            const configToSend = {
              type: 'PREVIEW_CONFIG',
              data: {
                logoUrl,
                headerUrl,
                accentColor: accentColor || '#F4A460', // Always send a valid color
                fontStyle: 'Arial', // Hardcoded
                customQuestions: customQuestions,
                customLinks: customLinks, // Pass current customLinks state
                templateConfig: templateConfig, // Pass template config
                activeTab: activeTab // Pass activeTab if needed by preview
              }
            };
            console.log(">>> Sending PREVIEW_CONFIG:", JSON.stringify(configToSend)); // Log the data

            iframe.contentWindow.postMessage(configToSend, PREVIEW_CONFIG.allowedOrigin); // Use the configured origin
            console.log("Sent preview configuration via postMessage");

            // Remove loading class after a delay
            // setTimeout(() => {
            //   if (previewColumn) {
            //     previewColumn.classList.remove('loading');
            //   }
            // }, 600);
            setIsPreviewLoading(false); // Stop loading after config is sent

          } catch (e) {
            console.error("Error sending preview config:", e);
            // if (previewColumn) {
            //   previewColumn.classList.remove('loading');
            // }
            setIsPreviewLoading(false); // Stop loading on error
          }
        } else {
          console.warn("PREVIEW_READY received, but iframe or contentWindow not found.");
          setIsPreviewLoading(false); // Stop loading if iframe not found
        }
      }
    };

    console.log("Setting up message listener for preview.");
    window.addEventListener('message', handleIframeMessage);

    // Cleanup function to remove the listener when SettingsPage unmounts
    return () => {
      console.log("Cleaning up message listener for preview.");
      window.removeEventListener('message', handleIframeMessage);
    };

    // Run this effect only once on component mount.
    // The handler itself will access the latest state values when invoked.
  }, [logoUrl, headerUrl, accentColor, customQuestions, customLinks, templateConfig, activeTab]); // Add templateConfig

  // Handle preview page change - triggers a refresh via key change
  const handlePreviewPageChange = (page) => {
    // console.log(`Changing preview page to: '${page}'`);
    setIsPreviewLoading(true); // Set loading true when page changes
    setPreviewPage(page);
    // The key change on the iframe will cause it to reload.
    // The message listener above will handle sending data when the new iframe is ready.
    // We also explicitly call triggerPreviewRefresh to ensure the loading state is managed
    // and the correct URL (with page param) is constructed if needed immediately.
    // However, the iframe's `key` prop changing should be the primary mechanism for reload.
    // The loading state will be turned off by the 'PREVIEW_READY' message handler.
  };

  // Effect to trigger preview refresh ONLY when settings change AFTER initial load
  useEffect(() => {
    // Skip the effect run caused by the initial fetchSettings
    if (isInitialMount.current) {
      isInitialMount.current = false; // Mark initial mount as complete
      return; // Don't trigger refresh on initial load
    }

    // Trigger refresh on subsequent changes to these dependencies
    console.log("Settings changed (logo, header, color, questions, links, or template), triggering preview refresh.");
    triggerPreviewRefresh();
  }, [logoUrl, headerUrl, accentColor, customQuestions, customLinks, templateConfig, triggerPreviewRefresh]); // Add templateConfig

  // Construct initial preview URL using PREVIEW_CONFIG
  const previewSrc = PREVIEW_CONFIG.previewUrl(businessId, previewPage);

  // Effect to handle iframe load event and turn off spinner
  useEffect(() => {
    const iframe = document.getElementById('frontrate-preview');
    const handleLoad = () => {
      // The PREVIEW_READY message handler is now responsible for turning off the spinner.
      // This is because 'load' might fire before the content inside is fully interactive.
      // console.log("Iframe loaded, but waiting for PREVIEW_READY to stop spinner.");
    };

    if (iframe) {
      iframe.addEventListener('load', handleLoad);
    }

    return () => {
      if (iframe) {
        iframe.removeEventListener('load', handleLoad);
      }
    };
  }, [previewSrc]); // Re-run when previewSrc changes (which includes previewPage)

  return (
    <div className="settings-page">
      {/* Header with breadcrumbs and personalized greeting */}
      <div className="settings-header">
        <div className="directory-info">
          <div className="directory-logo">
            <DirectoryLogo className="arrow-logo" />
            <a href='/'><p>{businessName}</p></a>
            <ArrowLogo />
            <div className="directory-logo">
              <DirectoryLogo />
            </div>
            <p>Settings</p>
          </div>
        </div>
        <h1>Hello, {name || '_name'}</h1>
      </div>

      <div className="settings-layout">
        <div className="settings-column">
          <div className="tab-navigation glass-box">
            <button 
              className={activeTab === 'account' ? 'active' : ''}
              onClick={() => setActiveTab('account')}
            >
              Account
            </button>
            <button 
              className={activeTab === 'business' ? 'active' : ''}
              onClick={() => setActiveTab('business')}
            >
              Business
            </button>
            <button 
              className={activeTab === 'survey' ? 'active' : ''}
              onClick={() => setActiveTab('survey')}
            >
              Survey Questions
            </button>
            <button 
              className={activeTab === 'referral' ? 'active' : ''}
              onClick={() => setActiveTab('referral')}
            >
              Referral Links
            </button>
            <button 
              className={activeTab === 'appearance' ? 'active' : ''}
              onClick={() => setActiveTab('appearance')}
            >
              Appearance
            </button>
            <button 
              className={activeTab === 'templates' ? 'active' : ''}
              onClick={() => setActiveTab('templates')}
            >
              Templates
            </button>
          </div>
          <div className="tab-content glass-box">
            {activeTab === 'account' && <AccountSettings name={name} setName={setName} email={email} setEmail={setEmail} />}
            {/* Pass triggerPreviewRefresh instead of handleRefreshPreview */}
            {activeTab === 'business' && <BusinessSettings
                                            businessId={businessId}
                                            setLogoUrl={setLogoUrl}
                                            setHeaderUrl={setHeaderUrl}
                                            setBusinessName={setBusinessName}
                                            businessName={businessName}
                                            onSettingsChange={triggerPreviewRefresh} // Use new function
                                            initialLogoUrl={logoUrl}
                                            initialHeaderUrl={headerUrl}
                                            initialBusinessNameFromServer={businessName}
                                          />}
            {/* Pass triggerPreviewRefresh instead of handleRefreshPreview */}
            {activeTab === 'survey' && <SurveyQuestionsSettings
                                          businessId={businessId}
                                          questions={customQuestions}
                                          setQuestions={setCustomQuestions}
                                          onSettingsChange={triggerPreviewRefresh} // Use new function
                                        />}
            {/* Pass links state, setter, and triggerPreviewRefresh */}
            {activeTab === 'referral' && <ReferralLinksSettings
                                            businessId={businessId}
                                            links={customLinks}
                                            setLinks={setCustomLinks}
                                            onSettingsChange={triggerPreviewRefresh} // Use new function
                                          />}
             {/* Pass triggerPreviewRefresh instead of handleRefreshPreview */}
            {activeTab === 'appearance' && <AppearanceSettings
                                              businessId={businessId}
                                              setAccentColor={(color) => {
                                                setAccentColor(color || '#F4A460'); // Always set a valid color
                                              }}
                                              accentColor={accentColor}
                                              onSettingsChange={triggerPreviewRefresh}
                                            />}
            {activeTab === 'templates' && <TemplateSettings
                                            businessId={businessId}
                                            onSettingsChange={triggerPreviewRefresh}
                                          />}
          </div>
        </div>

        <div className="preview-column">
          <div id="frontrate-preview-container">
            <div className="preview-controls glass-box">
              {/* Added Landing button */}
              <button 
                className={previewPage === '' ? 'active' : ''} 
                onClick={() => handlePreviewPageChange('')}
              >
                Landing
              </button>
              <button 
                className={previewPage === 'low-rating' ? 'active' : ''} 
                onClick={() => handlePreviewPageChange('low-rating')}
              >
                Survey
              </button>
              <button 
                className={previewPage === 'low-feedback' ? 'active' : ''} 
                onClick={() => handlePreviewPageChange('low-feedback')}
              >
                Feedback
              </button>
              <button 
                className={previewPage === 'low-thanx' ? 'active' : ''} 
                onClick={() => handlePreviewPageChange('low-thanx')}
              >
                Thank You
              </button>
              <button 
                className={previewPage === 'high-feedback' ? 'active' : ''} 
                onClick={() => handlePreviewPageChange('high-feedback')}
              >
                Review Links
              </button>
              <button className="refresh-preview" onClick={triggerPreviewRefresh}> {/* Use new function */}
                Refresh 🔄
              </button>
            </div>
            <div className="preview-device" style={{ position: 'relative' }}> {/* Added position relative for Spin overlay */}
              {isPreviewLoading && (
                <div className="preview-spin-overlay">
                  <Spin tip="Loading Preview..." size="large" />
                </div>
              )}
              <iframe
                id="frontrate-preview"
                key={previewPage} // Force remount/reload on page change
                src={previewSrc} // Use the base src here
                title="FrontRate Preview"
                width="540"
                height="880"
                sandbox="allow-scripts allow-same-origin allow-forms allow-modals" // Enhanced sandbox
                style={{ opacity: isPreviewLoading ? 0.5 : 1 }} // Optional: dim iframe while loading
              ></iframe>
            </div>
            <div className="preview-footer">
              <p>These changes will be applied to the survey your customers see</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;